import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { Alert, AlertTitle, alpha, Avatar, Box, Button, Chip, Link, Skeleton, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneDisabledOutlinedIcon from '@mui/icons-material/PhoneDisabledOutlined';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { useToast } from '../../../common/hooks/useToast';
import { Item } from '../../../styles/common';
import { FollowUpNotice, LogCallDialog } from '../calls/LogCallDialog';
import { CrmInvalidId, CrmPageFrame } from '../CrmPageFrame';
import { contactPath, CRM_PATHS, parseId, personCallsPath, returnTarget } from '../crmPaths';
import { countryName, describeListError, describeSupportLevel, errorCodeOf, formatRate } from '../contacts/contactList';
import { CRM_CONTACT_DETAIL } from './contactDetail.queries';
import {
  consentGates,
  ContactDetail,
  describeMatch,
  displayName,
  FLAG_WORDS,
  formatDay,
  personName,
  placeOf,
  shownFlags,
  SOURCE_WORDS,
} from './contactDetail';
import { ContactLists, ContactTags } from './ContactMembership';
import { ContactTimeline } from './ContactTimeline';
import { FlagBanners, MarkFlags } from './DoNotCall';
import { ConsentChange } from './edit/ConsentChange';
import { ContactNotes } from './edit/ContactNotes';
import { useContactPoints } from './edit/ContactPoints';
import { dialledNumber } from './edit/contactEdit';
import { EditDetailsDialog } from './edit/EditDetailsDialog';
import { cannotEmail, EmailPersonDialog } from './edit/EmailPersonDialog';
import { ContactWhatsApp, CRM_CONTACT_WHATSAPP, WhatsAppBanner, WhatsAppPersonDialog } from './edit/WhatsAppPersonDialog';

const STATUS_WORDS: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
  ACTIVE: { label: 'Active', color: 'success' },
  GRACE_PERIOD: { label: 'Grace period', color: 'warning' },
  EXPIRED: { label: 'Expired', color: 'default' },
  CANCELED: { label: 'Canceled', color: 'default' },
};

/** The memberships the person holds (card 20): plan, status, since and until. Batch updates add and change them. */
const Memberships = ({ contact }: { contact: ContactDetail }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
    {contact.memberships.map((m) => {
      const status = STATUS_WORDS[m.status] ?? { label: m.status, color: 'default' as const };
      return (
        <Box component="li" key={m.id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {m.planName}
          </Typography>
          <Chip size="small" label={status.label} color={status.color} variant="outlined" sx={{ height: 20, fontSize: '0.6875rem' }} />
          <Typography variant="caption" color="text.secondary">
            {[m.startedAt ? `since ${formatDay(m.startedAt)}` : '', m.expiresOn ? `until ${formatDay(m.expiresOn)}` : ''].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const n = (value: number | null | undefined) => (value ?? 0).toLocaleString('en-US');

/** A card on the page, with its heading. */
const Section = ({ title, children, id }: { title: string; children: React.ReactNode; id: string }) => (
  <Item sx={{ p: { xs: 1.5, sm: 2 } }}>
    <Box component="section" aria-labelledby={id}>
      <Typography id={id} variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.25 }}>
        {title}
      </Typography>
      {children}
    </Box>
  </Item>
);

/** Label and value pairs, two columns on wide screens. */
const Facts = ({ rows }: { rows: Array<[string, React.ReactNode]> }) => (
  <Box
    component="dl"
    sx={{
      m: 0,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, max-content) 1fr' },
      columnGap: 2,
      rowGap: 0.75,
    }}
  >
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <Typography component="dt" variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography
          component="dd"
          variant="body2"
          sx={{
            m: 0,
            mb: { xs: 0.75, sm: 0 },
            minWidth: 0,
            wordBreak: 'break-word',
          }}
        >
          {value === '' || value === null || value === undefined ? (
            <Box component="span" sx={{ color: '#cbd5e1' }}>
              —
            </Box>
          ) : (
            value
          )}
        </Typography>
      </React.Fragment>
    ))}
  </Box>
);

const Flag = ({ label, tone = 'default' }: { label: string; tone?: 'default' | 'error' | 'warning' }) => (
  <Chip size="small" label={label} color={tone} variant="outlined" sx={{ height: 20, fontSize: '0.6875rem', ml: 0.75 }} />
);

const initialsOf = (contact: ContactDetail) => {
  const words = displayName(contact).split(/\s+/).filter(Boolean);
  return `${words[0]?.[0] ?? '#'}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
};

/** A merged-away record's way to the contact it became. The do-not-call banner is DoNotCall.tsx's (card 20.5). */
const MergedBanner = ({ contact }: { contact: ContactDetail }) =>
  contact.mergedIntoId == null ? null : (
    <Alert
      severity="info"
      sx={{ mb: 2 }}
      action={
        <Button component={RouterLink} to={contactPath(contact.mergedIntoId)} color="inherit" size="small">
          Open it
        </Button>
      }
    >
      This record was merged into contact #{contact.mergedIntoId}. Its history shows there; tag and list that contact instead.
    </Alert>
  );

const Consent = ({ contact }: { contact: ContactDetail }) => (
  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
    {consentGates(contact).map((gate) => (
      <Box component="li" key={gate.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        {gate.allowed ? (
          <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20, mt: 0.125 }} aria-hidden />
        ) : (
          <RemoveCircleOutlineIcon sx={{ color: '#94a3b8', fontSize: 20, mt: 0.125 }} aria-hidden />
        )}
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {gate.allowed ? `Can ${gate.label.toLowerCase()}` : `Cannot ${gate.label.toLowerCase()}`}
          </Typography>
          {gate.reasons.length > 0 && (
            <Typography variant="caption" color="text.secondary" component="p">
              {gate.reasons.join(' · ')}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

const AppAccount = ({ contact }: { contact: ContactDetail }) => {
  const account = contact.account;
  if (!account) {
    return (
      <Typography variant="body2" color="text.secondary">
        No app account is linked to this person.
      </Typography>
    );
  }
  const name = personName(account) || account.username || `User #${account.id}`;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
        <Link component={RouterLink} to={`/users/${account.id}`} underline="hover" sx={{ fontWeight: 600 }}>
          {name}
        </Link>
        {account.isBanned && <Flag label="Banned" tone="error" />}
        {account.isDeleted && !account.isBanned && <Flag label="Deleted" tone="warning" />}
        {account.role === 'ADMIN' && <Flag label="Admin" />}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          mb: 1.5,
        }}
      >
        {[
          ['Points', n(account.totalPoints)],
          ['Referrals', n(account.referralCount)],
          ['Recommended', n(account.recommendationCount)],
        ].map(([label, value]) => (
          <Box
            key={label}
            sx={{
              bgcolor: '#f8fafc',
              border: '1px solid #eef1f5',
              borderRadius: 1.5,
              px: 1,
              py: 0.75,
            }}
          >
            <Typography variant="h6" component="p" sx={{ fontSize: '1rem', fontVariantNumeric: 'tabular-nums' }}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Facts
        rows={[
          ['Level', account.level ?? ''],
          ['Last seen', account.lastSeenAt ? formatDay(account.lastSeenAt) : 'Not since signing up'],
          ['Joined the app', formatDay(account.createdAt)],
          ['Username', account.username ?? ''],
          ['Email', account.email ?? ''],
          ['Phone', account.phone ?? ''],
        ]}
      />
      {describeMatch(contact) && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
          {describeMatch(contact)}.
        </Typography>
      )}
    </>
  );
};

/** Where they are, as the contact list filters on it: "Accra, Greater Accra, Ghana". */
const placeText = (contact: ContactDetail) =>
  [contact.city, contact.region, contact.countryCode ? countryName(contact.countryCode) : ''].filter(Boolean).join(', ');

/**
 * Who they are and how to reach them. Emails, numbers and addresses carry
 * their own menus (card 21.5: mark bad, correct, make main, remove, add).
 */
const Identity = ({ contact, onChanged }: { contact: ContactDetail; onChanged: () => void }) => {
  const points = useContactPoints({ contact, noCalls: contact.doNotCall || contact.doNotContact, onChanged });
  return (
    <>
      <Facts
        rows={[
          [emailLabel(contact), points.emails],
          [contact.phones.length + (contact.primaryPhone && !contact.phones.some((p) => p.e164 === contact.primaryPhone) ? 1 : 0) > 1 ? 'Phones' : 'Phone', points.phones],
          [contact.addresses.length > 1 ? 'Addresses' : 'Address', points.addresses],
          ['Place', placeText(contact)],
          ['Point person', personName(contact.owner)],
          ['Support level', describeSupportLevel(contact.supportLevel)],
          ['Stage', contact.lifecycleStage ?? ''],
          ['Came from', SOURCE_WORDS[contact.source] ?? contact.source],
          ['Joined', formatDay(contact.joinedAt)],
          ['Last contacted', formatDay(contact.lastContactedAt)],
          [
            'Calls',
            contact.callCount ? (
              // Card 21.6: every one of them, in the call log.
              <Link component={RouterLink} to={personCallsPath(contact.id)} underline="hover">
                {`${n(contact.callCount)}, last ${formatDay(contact.lastCalledAt)}`}
              </Link>
            ) : (
              'None'
            ),
          ],
        ]}
      />
      {points.dialogs}
      {contact.isPossibleDup && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0 }}>
          Possibly the same person as another record. Match review will decide.
        </Alert>
      )}
    </>
  );
};

const emailLabel = (contact: ContactDetail) =>
  contact.emails.length + (contact.primaryEmail && !contact.emails.some((e) => e.email === contact.primaryEmail) ? 1 : 0) > 1 ? 'Emails' : 'Email';

const Engagement = ({ contact }: { contact: ContactDetail }) => {
  const e = contact.engagement;
  if (!e || !e.emailsSent) {
    return (
      <Typography variant="body2" color="text.secondary">
        No emails sent to this person.
      </Typography>
    );
  }
  return (
    <Facts
      rows={[
        ['Sent', `${n(e.emailsSent)}, last ${formatDay(e.lastSentAt)}`],
        [
          'Opened',
          `${n(e.emailsOpened)}${e.engagementRate != null ? ` (${formatRate(e.engagementRate)})` : ''}${e.lastOpenedAt ? `, last ${formatDay(e.lastOpenedAt)}` : ''}`,
        ],
        ['Clicked', `${n(e.emailsClicked)}${e.lastClickedAt ? `, last ${formatDay(e.lastClickedAt)}` : ''}`],
        ['Bounced', n(e.emailsBounced)],
      ]}
    />
  );
};

const Loading = () => (
  <Box
    role="status"
    aria-label="Loading the contact…"
    sx={{
      display: 'grid',
      gap: 2,
      gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' },
    }}
  >
    <Item sx={{ p: 2 }}>
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} variant="text" width={`${80 - index * 7}%`} />
      ))}
    </Item>
    <Item sx={{ p: 2 }}>
      <Skeleton variant="rounded" height={90} />
    </Item>
  </Box>
);

/**
 * One person (card 19): who they are, whether they may be contacted and how,
 * their tags and lists (changed here), the linked app account, notes, and the
 * merged activity feed underneath. Card 21.5 makes the rest editable here:
 * details (Edit, or E), emails, numbers and addresses (each row's menu),
 * consent, and notes. Every change is on the timeline.
 *
 * Requests: the contact (one root field: its related lists are batched by the
 * API) and the timeline's first page with its counts. Both are asked fresh on
 * every visit, since calls and tags change while nobody looks at this page.
 */
export const CrmContactDetail = () => {
  const { contactId: param } = useParams();
  const location = useLocation();

  const contactId = parseId(param);
  const back = returnTarget(location.state);

  const { data, error, loading, refetch: refetchContact } = useQuery<{
    crmContact: ContactDetail;
  }>(CRM_CONTACT_DETAIL, {
    variables: { id: contactId },
    skip: contactId === null,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  // Whether they can be sent WhatsApp, decided by the API. A failure here (an
  // API without it yet) shows neither the button nor a banner.
  const whatsAppQuery = useQuery<{ crmContactWhatsApp: ContactWhatsApp }>(CRM_CONTACT_WHATSAPP, {
    variables: { contactId },
    skip: contactId === null,
    fetchPolicy: 'cache-and-network',
  });
  const whatsApp = whatsAppQuery.error ? undefined : whatsAppQuery.data?.crmContactWhatsApp;
  // Every change on this page can change who may be messaged (consent, do-not-contact, numbers).
  const refetch = () => {
    refetchContact();
    whatsAppQuery.refetch().catch(() => undefined);
  };

  const [logging, setLogging] = useState(false);
  const [editing, setEditing] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [whatsApping, setWhatsApping] = useState(false);
  const toast = useToast();
  const contactLoaded = data?.crmContact;
  const canLog = Boolean(contactLoaded && contactLoaded.mergedIntoId == null);

  // L logs a call and E edits their details (card 21.5), from anywhere on the page but a field.
  useEffect(() => {
    if (!canLog || logging || editing || emailing || whatsApping) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      const key = event.key.toLowerCase();
      if (typing || event.ctrlKey || event.metaKey || event.altKey || (key !== 'l' && key !== 'e')) return;
      // Not while a dialog or menu of the page's own is open.
      if (document.querySelector('[role="dialog"], [role="menu"]')) return;
      event.preventDefault();
      if (key === 'l') setLogging(true);
      else setEditing(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canLog, logging, editing, emailing, whatsApping]);

  if (contactId === null) {
    return <CrmInvalidId param={param} noun="contact" listLabel="Contacts" listPath={CRM_PATHS.contacts} />;
  }

  const contact = data?.crmContact;
  const notFound = errorCodeOf(error) === 'CRM_CONTACT_NOT_FOUND';

  return (
    <CrmPageFrame
      title={contact ? displayName(contact) : `Contact #${contactId}`}
      description={
        contact
          ? [placeOf(contact), `Contact #${contact.id}`, contact.nbSignupId ? `NationBuilder #${contact.nbSignupId}` : '']
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      crumbs={[{ label: back.fromLabel, to: back.from }, { label: `#${contactId}` }]}
      actions={
        <>
          <Button component={RouterLink} to={back.from} variant="outlined" size="small">
            Back to {back.fromLabel.toLowerCase()}
          </Button>
          {contact?.account && (
            <Button component={RouterLink} to={`/users/${contact.account.id}`} variant="outlined" size="small" startIcon={<PersonOutlineIcon />}>
              Open user page
            </Button>
          )}
          {contact && (
            <Tooltip describeChild title={canLog ? 'Key: L' : 'Merged into another contact: log the call there.'}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PhoneInTalkOutlinedIcon />}
                  onClick={() => setLogging(true)}
                  disabled={!canLog}
                >
                  Log a call
                </Button>
              </span>
            </Tooltip>
          )}
          {contact && (
            <Tooltip describeChild title={cannotEmail(contact) ?? `Write to ${contact.primaryEmail}`}>
              <span>
                <Button variant="outlined" size="small" startIcon={<SendOutlinedIcon />} onClick={() => setEmailing(true)} disabled={!!cannotEmail(contact)}>
                  Send email
                </Button>
              </span>
            </Tooltip>
          )}
          {contact && whatsApp?.canSend && (
            <Button variant="outlined" size="small" startIcon={<WhatsAppIcon />} onClick={() => setWhatsApping(true)}>
              Send WhatsApp
            </Button>
          )}
        </>
      }
    >
      {notFound ? (
        <Alert
          severity="warning"
          action={
            <Button component={RouterLink} to={back.from} color="inherit" size="small">
              Back to {back.fromLabel.toLowerCase()}
            </Button>
          }
        >
          <AlertTitle>No contact #{contactId}</AlertTitle>
          It may have been deleted, or the link is wrong.
        </Alert>
      ) : error && !contact ? (
        <Alert severity="error">{describeListError(error).replace('contacts', 'this contact')}</Alert>
      ) : !contact ? (
        loading && <Loading />
      ) : (
        <>
          <FlagBanners contact={contact} onChanged={() => refetch()} />
          <MergedBanner contact={contact} />
          <WhatsAppBanner status={whatsApp} name={contact.firstName?.trim() || displayName(contact)} />
          {contact.followUp && (
            <FollowUpNotice
              followUp={contact.followUp}
              canLog={canLog}
              onLog={() => setLogging(true)}
              onClosed={() => refetch()}
            />
          )}
          {editing && (
            <EditDetailsDialog
              contact={contact}
              onClose={() => setEditing(false)}
              onSaved={() => {
                setEditing(false);
                refetch();
              }}
            />
          )}
          {emailing && contact.primaryEmail && (
            <EmailPersonDialog
              contactId={contact.id}
              name={contact.firstName?.trim() || displayName(contact)}
              email={contact.primaryEmail}
              onClose={() => setEmailing(false)}
              onSent={(dryRun) => {
                setEmailing(false);
                if (dryRun) toast.warn('Logged, not delivered: this server only logs email, so nothing was sent and no note was kept.');
                else toast.success(`Sent to ${contact.primaryEmail}. A copy is in their notes.`);
                refetch();
              }}
            />
          )}
          {whatsApping && whatsApp?.canSend && (
            <WhatsAppPersonDialog
              contactId={contact.id}
              name={contact.firstName?.trim() || displayName(contact)}
              status={whatsApp}
              onClose={() => setWhatsApping(false)}
              onSent={() => refetch()}
            />
          )}
          {logging && (
            <LogCallDialog
              contactId={contact.id}
              name={contact.firstName?.trim() || displayName(contact)}
              number={dialledNumber(contact)}
              blocked={contact.doNotContact ? 'doNotContact' : contact.doNotCall ? 'doNotCall' : null}
              onClose={() => setLogging(false)}
              onLogged={() => {
                setLogging(false);
                refetch();
              }}
            />
          )}
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 360px' },
              alignItems: 'start',
            }}
          >
            <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
              <Item sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Box component="section" aria-label="Who">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Avatar
                      aria-hidden
                      sx={(theme) => ({
                        width: 48,
                        height: 48,
                        fontWeight: 600,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: 'primary.main',
                      })}
                    >
                      {initialsOf(contact)}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" component="h2" sx={{ lineHeight: 1.3 }}>
                        {[contact.prefix, contact.firstName, contact.middleName, contact.lastName, contact.suffix]
                          .filter(Boolean)
                          .join(' ') || displayName(contact)}
                      </Typography>
                      {shownFlags(contact).map((field) => (
                        <Chip
                          key={field}
                          size="small"
                          color="error"
                          icon={<PhoneDisabledOutlinedIcon />}
                          label={FLAG_WORDS[field].title}
                          sx={{ height: 22, fontWeight: 600, mb: 0.25 }}
                        />
                      ))}
                      <Typography variant="body2" color="text.secondary">
                        {contact.account ? 'App member' : 'No app account'}
                        {contact.nbCreatedAt ? ` · NationBuilder since ${formatDay(contact.nbCreatedAt)}` : ''}
                      </Typography>
                    </Box>
                    {canLog && (
                      <Tooltip describeChild title="Name, place, point person, support level, stage. Key: E">
                        <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setEditing(true)} sx={{ alignSelf: 'flex-start', flexShrink: 0 }}>
                          Edit
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                  <Identity contact={contact} onChanged={() => refetch()} />
                </Box>
              </Item>

              <Section title="Activity" id="crm-contact-activity">
                <ContactTimeline contactId={contact.id} />
              </Section>
            </Box>

            <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
              <Section title="Consent" id="crm-contact-consent">
                <Consent contact={contact} />
                <MarkFlags contact={contact} onChanged={() => refetch()} />
                <ConsentChange contact={contact} onChanged={() => refetch()} />
              </Section>
              <Section title="Tags" id="crm-contact-tags">
                <ContactTags contact={contact} />
              </Section>
              <Section title="Lists" id="crm-contact-lists">
                <ContactLists contact={contact} onChanged={() => refetch()} />
              </Section>
              {contact.memberships.length > 0 && (
                <Section title={contact.memberships.length > 1 ? 'Memberships' : 'Membership'} id="crm-contact-memberships">
                  <Memberships contact={contact} />
                </Section>
              )}
              <Section title="App account" id="crm-contact-account">
                <AppAccount contact={contact} />
              </Section>
              <Section title="Email" id="crm-contact-email">
                <Engagement contact={contact} />
              </Section>
              <Section title={contact.notes.length === 1 ? 'Note' : 'Notes'} id="crm-contact-notes">
                <ContactNotes contact={contact} onChanged={() => refetch()} />
              </Section>
            </Box>
          </Box>
        </>
      )}
    </CrmPageFrame>
  );
};
